import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Search, Users, BookOpen, CornerDownLeft } from 'lucide-react'
import { listClients, listAllProjects } from '../lib/api.js'

/** Global ⌘K / Ctrl+K quick-jump — search across every client and project
 *  without navigating menus. Mounted once inside AppShell (authenticated
 *  screens only, since it needs org-scoped data). */
export default function CommandPalette({ open, onOpenChange }) {
  const setOpen = onOpenChange
  const [query, setQuery] = useState('')
  const [items, setItems] = useState(null)
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    function onKeyDown(e) {
      const isMeta = e.metaKey || e.ctrlKey
      if (isMeta && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (!open) { setQuery(''); setActiveIdx(0); return }
    setTimeout(() => inputRef.current?.focus(), 30)
    Promise.all([listClients(), listAllProjects()]).then(([clients, projects]) => {
      setItems([
        ...clients.map((c) => ({ type: 'client', id: c.id, label: c.name, sub: 'Client', to: `/clients/${c.id}` })),
        ...projects.map((p) => ({ type: 'project', id: p.id, label: p.title, sub: p.clients?.name ? `Project · ${p.clients.name}` : 'Project', to: `/editor/${p.id}` }))
      ])
    })
  }, [open])

  const filtered = useMemo(() => {
    if (!items) return []
    const q = query.trim().toLowerCase()
    if (!q) return items.slice(0, 8)
    return items.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 8)
  }, [items, query])

  function go(item) {
    setOpen(false)
    navigate(item.to)
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && filtered[activeIdx]) { go(filtered[activeIdx]) }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-start justify-center pt-[14vh] px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            className="relative glass rounded-lg shadow-elev3 w-full max-w-[520px] overflow-hidden"
            initial={{ opacity: 0, scale: 0.97, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -8 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-line">
              <Search size={17} className="text-muted-2 shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActiveIdx(0) }}
                onKeyDown={handleKeyDown}
                placeholder="Jump to a client or project…"
                className="flex-1 bg-transparent outline-none text-[14.5px] placeholder:text-muted-2"
              />
              <kbd className="text-[10px] text-muted-2 border border-line rounded px-1.5 py-0.5">ESC</kbd>
            </div>

            <div className="max-h-[320px] overflow-y-auto py-1.5">
              {items === null ? (
                <div className="px-4 py-6 text-center text-muted-2 text-[13px]">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-6 text-center text-muted-2 text-[13px]">No matches for "{query}"</div>
              ) : (
                filtered.map((item, i) => {
                  const Icon = item.type === 'client' ? Users : BookOpen
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={() => go(item)}
                      onMouseEnter={() => setActiveIdx(i)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100 ${i === activeIdx ? 'bg-panel2' : ''}`}
                    >
                      <Icon size={15} className="text-sky shrink-0" />
                      <span className="flex-1 min-w-0 text-[13.5px] truncate">{item.label}</span>
                      <span className="text-[11px] text-muted-2 shrink-0">{item.sub}</span>
                      {i === activeIdx && <CornerDownLeft size={13} className="text-muted-2 shrink-0" />}
                    </button>
                  )
                })
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
